import { PlusCircle, Eye } from "react-feather";
import { Link } from "react-router-dom";
import { Select, DatePicker, Tabs, Modal, Table, Popconfirm } from "antd";
import React, { useState, useEffect } from "react";
import {
  getAllReviewPeriods,
  deleteReviewPeriod,
  deleteAppraisal,
  getAppraisalList,
  getUniqueAppraisalsByReviewPeriod,
} from "../../service/appraisalService";
import { getAllDepartments } from "../../service/ApiServices";
import { ArrowLeft } from "lucide-react";
import moment from "moment";
import CreateReviewPeriod from "../../core/modals/appraisal/CreateReviewPeriod";
import AssignAppraisal from "../../core/modals/appraisal/AssignAppraisal";

const AppraisalList = () => {
  const [openCreateReviewModal, setOpenCreateReviewModal] = useState(false);
  const [openAssignAppraisalModal, setOpenAssignAppraisalModal] = useState(false);
  const [openViewAppraisalsModal, setOpenViewAppraisalsModal] = useState(false);
  const [selectedReviewPeriod, setSelectedReviewPeriod] = useState(null);
  const [selectedAppraisal, setSelectedAppraisal] = useState(null);
  const [reviewPeriods, setReviewPeriods] = useState([]);
  const [appraisals, setAppraisals] = useState([]);
  const [uniqueAppraisals, setUniqueAppraisals] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchReviewName, setSearchReviewName] = useState("");
  const [searchAppraisalName, setSearchAppraisalName] = useState("");
  const [searchAppraisalEmployee, setSearchAppraisalEmployee] = useState("");
  const [searchAppraisalEpf, setSearchAppraisalEpf] = useState("");
  const [searchReviewDate, setSearchReviewDate] = useState(null);
  const [searchAppraisalPeriod, setSearchAppraisalPeriod] = useState("");
  const [searchAppraisalDepartment, setSearchAppraisalDepartment] = useState("");
  const [paginationReviews, setPaginationReviews] = useState({ current: 1, pageSize: 5, total: 0 });
  const [paginationAppraisals, setPaginationAppraisals] = useState({ current: 1, pageSize: 5, total: 0 });
  const [paginationUniqueAppraisals, setPaginationUniqueAppraisals] = useState({ current: 1, pageSize: 5, total: 0 });
  const [pageSizeModalVisible, setPageSizeModalVisible] = useState(false);
  const [tempPageSize, setTempPageSize] = useState(5);
  const [loading, setLoading] = useState(false);
  const pageSizeOptions = [5, 10, 20, 50, 100];

  useEffect(() => {
    const fetchReviewPeriods = async () => {
      setLoading(true);
      try {
        const data = await getAllReviewPeriods();
        setReviewPeriods(data);
        setPaginationReviews((prev) => ({ ...prev, total: data.length }));
      } catch (error) {
        console.error("Error fetching review periods:", error);
        setReviewPeriods([]);
      } finally {
        setLoading(false);
      }
    };
    fetchReviewPeriods();
  }, []);

  useEffect(() => {
    const fetchAppraisals = async () => {
      setLoading(true);
      try {
        const data = await getAppraisalList();
        setAppraisals(data);
        setPaginationAppraisals((prev) => ({ ...prev, total: data.length }));
      } catch (error) {
        console.error("Error fetching appraisals:", error);
        setAppraisals([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAppraisals();
  }, []);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        await getAllDepartments({
          search: "",
          setLoading,
          setData: setDepartments,
        });
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };
    fetchDepartments();
  }, []);

  const fetchReviewPeriods = async () => {
    setLoading(true);
    try {
      const data = await getAllReviewPeriods();
      setReviewPeriods(data);
      setPaginationReviews((prev) => ({ ...prev, total: data.length }));
    } catch (error) {
      console.error("Error fetching review periods:", error);
      setReviewPeriods([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAppraisals = async () => {
    setLoading(true);
    try {
      const data = await getAppraisalList();
      setAppraisals(data);
      setPaginationAppraisals((prev) => ({ ...prev, total: data.length }));
    } catch (error) {
      console.error("Error fetching appraisals:", error);
      setAppraisals([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUniqueAppraisals = async (periodId) => {
    setLoading(true);
    try {
      const data = await getUniqueAppraisalsByReviewPeriod(periodId, {
        appraisalName: searchAppraisalName,
        departmentName: searchAppraisalDepartment,
      });
      setUniqueAppraisals(data);
      setPaginationUniqueAppraisals((prev) => ({ ...prev, total: data.length }));
    } catch (error) {
      console.error("Error fetching unique appraisals:", error);
      setUniqueAppraisals([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination, type) => {
    if (type === "reviews") {
      setPaginationReviews(pagination);
    } else if (type === "appraisals") {
      setPaginationAppraisals(pagination);
    } else if (type === "uniqueAppraisals") {
      setPaginationUniqueAppraisals(pagination);
    }
  };

  const handlePageSizeChange = () => {
    const newPageSize = tempPageSize;
    setPaginationReviews({ ...paginationReviews, pageSize: newPageSize, current: 1 });
    setPaginationAppraisals({ ...paginationAppraisals, pageSize: newPageSize, current: 1 });
    setPaginationUniqueAppraisals({ ...paginationUniqueAppraisals, pageSize: newPageSize, current: 1 });
    setPageSizeModalVisible(false);
  };

  const columnsReviews = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      render: (date) => (date ? moment(date).format("YYYY-MM-DD") : "N/A"),
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      render: (date) => (date ? moment(date).format("YYYY-MM-DD") : "N/A"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Action",
      key: "action",
      render: (record) => (
        <td className="action-table-data">
          <Link
            className="me-2 p-2"
            to="#"
            onClick={() => {
              setSelectedReviewPeriod(record);
              setOpenCreateReviewModal(true);
            }}
            title="Edit review period"
          >
            <i data-feather="edit" className="feather-edit"></i>
          </Link>
          <Link
            className="me-2 p-2"
            to="#"
            onClick={() => {
              setSelectedReviewPeriod(record);
              fetchUniqueAppraisals(record._id);
              setOpenViewAppraisalsModal(true);
            }}
            title="View assigned appraisals"
          >
            <Eye className="feather-eye" />
          </Link>
          <Popconfirm
            title="Delete the Review Period"
            description="Are you sure to delete this review period?"
            onConfirm={async () => {
              try {
                await deleteReviewPeriod(record._id);
                await fetchReviewPeriods();
              } catch (error) {
                console.error("Error deleting review period:", error);
              }
            }}
            okText="Yes"
            cancelText="No"
          >
            <Link className="confirm-text p-2" title="Delete review period">
              <i data-feather="trash-2" className="feather-trash-2"></i>
            </Link>
          </Popconfirm>
        </td>
      ),
    },
  ];

  const columnsAppraisals = [
    {
      title: "Appraisal Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Employee Name",
      dataIndex: "employee_id",
      key: "employee_id",
      render: (employee) => employee?.full_name || "N/A",
    },
    {
      title: "EPF Number",
      dataIndex: "employee_id",
      key: "epf_number",
      render: (employee) => employee?.epf_number || "N/A",
    },
    {
      title: "Department",
      dataIndex: "department_id",
      key: "department_id",
      render: (department) => department?.departmentName || "N/A",
    },
    {
      title: "Review Period",
      dataIndex: "period_id",
      key: "period_id",
      render: (period) => period?.name || "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Action",
      key: "action",
      render: (record) => (
        <td className="action-table-data">
          <Link
            className="me-2 p-2"
            to="#"
            onClick={() => {
              setSelectedAppraisal(record);
              setOpenAssignAppraisalModal(true);
            }}
            title="Edit appraisal"
          >
            <i data-feather="edit" className="feather-edit"></i>
          </Link>
          <Popconfirm
            title="Delete the Appraisal"
            description="Are you sure to delete this appraisal?"
            onConfirm={async () => {
              try {
                await deleteAppraisal(record._id);
                await fetchAppraisals();
              } catch (error) {
                console.error("Error deleting appraisal:", error);
              }
            }}
            okText="Yes"
            cancelText="No"
          >
            <Link className="confirm-text p-2" title="Delete appraisal">
              <i data-feather="trash-2" className="feather-trash-2"></i>
            </Link>
          </Popconfirm>
        </td>
      ),
    },
  ];

  const columnsUniqueAppraisals = [
    {
      title: "Appraisal Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Review Period",
      dataIndex: "reviewPeriod",
      key: "reviewPeriod",
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
    },
  ];

  const handleSearchReviews = async () => {
    setLoading(true);
    try {
      const data = await getAllReviewPeriods();
      const filtered = data.filter((period) => {
        const matchesName = period.name
          ?.toLowerCase()
          .includes(searchReviewName.toLowerCase());
        const matchesDate = searchReviewDate
          ? moment(period.startDate).isSame(searchReviewDate, "day") ||
            moment(period.endDate).isSame(searchReviewDate, "day")
          : true;
        return matchesName && matchesDate;
      });
      setReviewPeriods(filtered);
      setPaginationReviews((prev) => ({ ...prev, total: filtered.length, current: 1 }));
    } catch (error) {
      console.error("Error searching review periods:", error);
      setReviewPeriods([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchAppraisals = async () => {
    setLoading(true);
    try {
      const data = await getAppraisalList();
      const filtered = data.filter((appraisal) => {
        const matchesAppraisalName = appraisal.name
          ?.toLowerCase()
          .includes(searchAppraisalName.toLowerCase());
        const matchesEmployee = appraisal.employee_id?.full_name
          ?.toLowerCase()
          .includes(searchAppraisalEmployee.toLowerCase());
        const matchesEpf = searchAppraisalEpf
          ? appraisal.employee_id?.epf_number
              ?.toLowerCase()
              .includes(searchAppraisalEpf.toLowerCase())
          : true;
        const matchesPeriod = appraisal.period_id?.name
          ?.toLowerCase()
          .includes(searchAppraisalPeriod.toLowerCase());
        const matchesDepartment = searchAppraisalDepartment
          ? appraisal.department_id?.departmentName
              ?.toLowerCase()
              .includes(searchAppraisalDepartment.toLowerCase())
          : true;
        return matchesAppraisalName && matchesEmployee && matchesEpf && matchesPeriod && matchesDepartment;
      });
      setAppraisals(filtered);
      setPaginationAppraisals((prev) => ({ ...prev, total: filtered.length, current: 1 }));
    } catch (error) {
      console.error("Error searching appraisals:", error);
      setAppraisals([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-wrapper-lt">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="header-leave-container d-flex align-items-center">
              <div>
                <ArrowLeft size={32} />
              </div>
              <div className="page-title">
                <h4>Appraisal List</h4>
                <h6>Manage review periods and appraisals</h6>
              </div>
            </div>
            <div className="page-btn">
              <a
                className="btn btn-added-grid"
                onClick={() => {
                  setSelectedReviewPeriod(null);
                  setOpenCreateReviewModal(true);
                }}
                title="Create new review period"
              >
                <PlusCircle className="me-2" />
                Period
              </a>
            </div>
            <div className="page-btn">
              <a
                className="btn btn-added"
                onClick={() => {
                  setSelectedAppraisal(null);
                  setOpenAssignAppraisalModal(true);
                }}
                title="Assign new appraisal"
              >
                <PlusCircle className="me-2" />
                KPI
              </a>
            </div>
          </div>
        </div>
        <Tabs
          defaultActiveKey="1"
          items={[
            {
              label: "Review Periods",
              key: "1",
              children: (
                <div>
                  <div className="card table-list-card-grid">
                    <div className="card-body" style={{ maxWidth: "100%", marginRight: "20px" }}>
                      <div
                        className="container mt-3"
                        style={{ maxWidth: "100%", marginLeft: "20px", marginRight: "40px" }}
                      >
                        <div className="row d-flex align-items-end">
                          <div className="col-12 col-md-3 text-start">
                            <label className="form-label">Review Period Name</label>
                            <input
                              type="text"
                              value={searchReviewName}
                              className="form-control"
                              onChange={(e) => setSearchReviewName(e.target.value)}
                              style={{ width: "100%", height: "40px" }}
                            />
                          </div>
                          <div className="col-12 col-md-3 text-start">
                            <label className="form-label">Date</label>
                            <DatePicker
                              className="form-control"
                              placeholder="Select date"
                              value={searchReviewDate}
                              onChange={(date) => setSearchReviewDate(date)}
                              style={{ width: "100%", height: "40px" }}
                              format="YYYY-MM-DD"
                            />
                          </div>
                          <div className="col-12 col-md-3 text-start">
                            <button
                              disabled={loading}
                              className="btn btn-submit"
                              onClick={handleSearchReviews}
                              style={{ height: "40px", width: "100px" }}
                            >
                              {loading ? "Searching..." : "Search"}
                            </button>
                            {loading && (
                              <div className="spinner">
                                <div className="loading-circle"></div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="table-top"></div>
                      <div className="table-responsive">
                        <Table
                          loading={loading}
                          columns={columnsReviews}
                          dataSource={reviewPeriods}
                          rowKey="_id"
                          pagination={{
                            current: paginationReviews.current,
                            pageSize: paginationReviews.pageSize,
                            pageSizeOptions: ["5", "10", "20", "50"],
                            total: paginationReviews.total,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total) => `Total ${total} items`,
                          }}
                          onChange={(pagination) => handleTableChange(pagination, "reviews")}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ),
            },
            {
              label: "Appraisals",
              key: "2",
              children: (
                <div>
                  <div className="card table-list-card-grid">
                    <div className="card-body" style={{ maxWidth: "100%", marginRight: "20px" }}>
                      <div
                        className="container mt-3"
                        style={{ maxWidth: "100%", marginLeft: "20px", marginRight: "40px" }}
                      >
                        <div className="row d-flex align-items-end">
                          <div className="col-12 col-md-2 text-start">
                            <label className="form-label">Appraisal Name</label>
                            <input
                              type="text"
                              value={searchAppraisalName}
                              className="form-control"
                              onChange={(e) => setSearchAppraisalName(e.target.value)}
                              style={{ width: "100%", height: "40px" }}
                            />
                          </div>
                          <div className="col-12 col-md-2 text-start">
                            <label className="form-label">Employee Name</label>
                            <input
                              type="text"
                              value={searchAppraisalEmployee}
                              className="form-control"
                              onChange={(e) => setSearchAppraisalEmployee(e.target.value)}
                              style={{ width: "100%", height: "40px" }}
                            />
                          </div>
                          <div className="col-12 col-md-2 text-start">
                            <label className="form-label">EPF Number</label>
                            <input
                              type="text"
                              value={searchAppraisalEpf}
                              className="form-control"
                              onChange={(e) => setSearchAppraisalEpf(e.target.value)}
                              style={{ width: "100%", height: "40px" }}
                            />
                          </div>
                          <div className="col-12 col-md-2 text-start">
                            <label className="form-label">Review Period</label>
                            <Select
                              className="select"
                              options={reviewPeriods.map((period) => ({
                                value: period.name,
                                label: period.name,
                              }))}
                              placeholder="Select review period"
                              value={searchAppraisalPeriod}
                              onChange={setSearchAppraisalPeriod}
                              allowClear
                              showSearch
                              filterOption={(input, option) =>
                                option.label.toLowerCase().includes(input.toLowerCase())
                              }
                              style={{ width: "100%", height: "40px" }}
                            />
                          </div>
                          <div className="col-12 col-md-2 text-start">
                            <label className="form-label">Department</label>
                            <Select
                              className="select"
                              options={departments.map((dept) => ({
                                value: dept.departmentName,
                                label: dept.departmentName,
                              }))}
                              placeholder="Select department"
                              value={searchAppraisalDepartment}
                              onChange={setSearchAppraisalDepartment}
                              allowClear
                              showSearch
                              filterOption={(input, option) =>
                                option.label.toLowerCase().includes(input.toLowerCase())
                              }
                              style={{ width: "100%", height: "40px" }}
                            />
                          </div>
                          <div className="col-12 col-md-2 text-start">
                            <button
                              disabled={loading}
                              className="btn btn-submit"
                              onClick={handleSearchAppraisals}
                              style={{ height: "40px", width: "100px" }}
                            >
                              {loading ? "Searching..." : "Search"}
                            </button>
                            {loading && (
                              <div className="spinner">
                                <div className="loading-circle"></div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="table-top"></div>
                      <div className="table-responsive">
                        <Table
                          loading={loading}
                          columns={columnsAppraisals}
                          dataSource={appraisals}
                          rowKey="_id"
                          pagination={{
                            current: paginationAppraisals.current,
                            pageSize: paginationAppraisals.pageSize,
                            pageSizeOptions: ["5", "10", "20", "50"],
                            total: paginationAppraisals.total,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total) => `Total ${total} items`,
                          }}
                          onChange={(pagination) => handleTableChange(pagination, "appraisals")}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ),
            },
          ]}
        />
      </div>

      <Modal
        zIndex={200000}
        open={openCreateReviewModal}
        onCancel={() => setOpenCreateReviewModal(false)}
        footer={null}
        maskClosable={false}
        closable={false}
      >
        <CreateReviewPeriod
          selectedReviewPeriod={selectedReviewPeriod}
          handleCancel={() => {
            setSelectedReviewPeriod(null);
            setOpenCreateReviewModal(false);
          }}
          handleReload={fetchReviewPeriods}
        />
      </Modal>

      <Modal
        zIndex={200000}
        open={openAssignAppraisalModal}
        onCancel={() => setOpenAssignAppraisalModal(false)}
        footer={null}
        maskClosable={false}
        closable={false}
      >
        <AssignAppraisal
          selectedAppraisal={selectedAppraisal}
          handleCancel={() => {
            setSelectedAppraisal(null);
            setOpenAssignAppraisalModal(false);
          }}
          handleReload={fetchAppraisals}
          reviewPeriods={reviewPeriods}
          departments={departments}
        />
      </Modal>

      <Modal
        zIndex={200000}
        title="Assigned Appraisals"
        open={openViewAppraisalsModal}
        onCancel={() => {
          setOpenViewAppraisalsModal(false);
          setSelectedReviewPeriod(null);
          setUniqueAppraisals([]);
        }}
        footer={null}
        width={800}
      >
        <div className="card-body">
          <div className="table-responsive">
            <Table
              loading={loading}
              columns={columnsUniqueAppraisals}
              dataSource={uniqueAppraisals}
              rowKey="name"
              pagination={{
                current: paginationUniqueAppraisals.current,
                pageSize: paginationUniqueAppraisals.pageSize,
                pageSizeOptions: ["5", "10", "20", "50"],
                total: paginationUniqueAppraisals.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} items`,
              }}
              onChange={(pagination) => handleTableChange(pagination, "uniqueAppraisals")}
            />
          </div>
        </div>
      </Modal>

      <Modal
        title="Change Page Size"
        open={pageSizeModalVisible}
        onOk={handlePageSizeChange}
        onCancel={() => setPageSizeModalVisible(false)}
        footer={[
          <button
            key="cancel"
            className="btn btn-cancel"
            onClick={() => setPageSizeModalVisible(false)}
          >
            Cancel
          </button>,
          <button key="submit" className="btn btn-submit" onClick={handlePageSizeChange}>
            Apply
          </button>,
        ]}
      >
        <div className="form-group">
          <label>Items per page:</label>
          <Select
            style={{ width: "100%" }}
            value={tempPageSize}
            onChange={(value) => setTempPageSize(value)}
          >
            {pageSizeOptions.map((size) => (
              <Select.Option key={size} value={size}>
                {size}
              </Select.Option>
            ))}
          </Select>
        </div>
      </Modal>
    </div>
  );
};

export default AppraisalList;